import {
  Component,
  EventEmitter,
  forwardRef, inject,
  Input,
  Output
} from '@angular/core';
import {CmsApiService} from '../../core/cms-api.service';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {FieldValidationService} from '../../core/field-validation.service';
import {FieldValueService} from '../../core/field-value.service';
import {InFocus} from '../in-focus';
import {GenerateIdentifierParams} from '../../core/definitions/generate-identifier-params';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {MetaTypes} from "../../core/definitions/meta-types";
import {System} from "../../core/definitions/system";

@Component({
    selector: 'app-edit-field-identifier',
    templateUrl: './edit-field-identifier.component.html',
    styleUrls: ['./edit-field-identifier.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => EditFieldIdentifierComponent)
        }
    ],
    standalone: false
})
export class EditFieldIdentifierComponent implements ControlValueAccessor {
  // Injections
  public readonly fieldValidation: FieldValidationService = inject(FieldValidationService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly fieldValueService: FieldValueService = inject(FieldValueService);

  @Input() fieldParameters: FieldParameters;
  @Input() formControlName: string;
  @Input() placeholder: string;
  @Input() inFocus: InFocus;
  @Output() fieldFocus = new EventEmitter<object>();
  @Output() fieldBlur = new EventEmitter<object>();

  onChange: any;

  async generateAnIdentifier() {
    if (!this.isDisabled) {
      await this.generateIdentifier();
    }
  }

  get isDisabled() {
    const rootObj = this.fieldParameters.sectionsContainer.rootObject;
    return !!rootObj.identification_number?.identification_number || !rootObj.collection?.collection_id;
  }

  private async generateIdentifier() {
    const sectionsContainer = this.fieldParameters.sectionsContainer;
    const params = new GenerateIdentifierParams();
    params.superobject_type_id = sectionsContainer.rootObject.superobject_type_id;
    if (sectionsContainer.rootObject.meta_type !== MetaTypes.SPECTRUM_PROCEDURE) {
      params.collection_id = sectionsContainer.rootObject.collection.collection_id;
    }
    if (sectionsContainer.rootObject.contexts) {
      sectionsContainer.rootObject.contexts.forEach(context => {
        if (context.context_id) {
          params.context_id = context.context_id;
        }
      });
    }
    if (sectionsContainer.useContextCreatingIdentifier && sectionsContainer.operationContextObjects) {
      params.context_id = sectionsContainer.operationContextObjects[0].artifact_id;
    }
    try {
      const data = await this.cms.getNextIdentifier(params);
      const formControl = sectionsContainer.formGroup.controls[this.formControlName];
      const autoGenFormControlName = this.formControlName.split(System.keySep)[0] + System.keySep + 'auto_generated';
      const autoGenFormControl= sectionsContainer.formGroup.controls[autoGenFormControlName];
      formControl.setValue(data.identifier);
      formControl.markAsDirty();
      this.setAutoGenerated(true)
      this.fieldValueService.setFieldValue(sectionsContainer.rootObject, this.formControlName, data.identifier);
    } catch (response) {
      console.error('Unable to generate identifier: ' + response.message);
    }
  }

  private setAutoGenerated(value: boolean) {
    const autoGenFormControlName = this.formControlName.split(System.keySep)[0] + System.keySep + 'auto_generated';
    const autoGenFormControl= this.fieldParameters.sectionsContainer.formGroup.controls[autoGenFormControlName];
    autoGenFormControl.setValue(value);
    this.fieldValueService.setFieldValue(this.fieldParameters.sectionsContainer.rootObject, autoGenFormControlName, value);
    console.log('Auto generated: ' + this.fieldParameters.sectionsContainer.rootObject.identification_number?.auto_generated);
  }

  onValueChange($event: any) {
    this.fieldValueService.setFieldValue(this.fieldParameters.sectionsContainer.rootObject, this.formControlName, $event.target.value);
    this.setAutoGenerated(false);
  }

  clearValue() {
    this.fieldParameters.sectionsContainer.formGroup.controls[this.formControlName].setValue('');
    this.fieldValueService.setFieldValue(this.fieldParameters.sectionsContainer.rootObject, this.formControlName, '');
    this.setAutoGenerated(false);
  }

  async pressedKey(evt: any) {
    if (evt.key === 'Enter') { // enter
      await this.generateAnIdentifier();
      evt.preventDefault();
    }

  }

  onFieldIdentifierBlur() {
    this.fieldBlur.emit();
  }

  onFieldIdentifierFocus() {
    this.fieldFocus.emit();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(/*fn: any*/): void {
    // No need to implement anything here
  }

  setDisabledState(/*isDisabled: boolean*/): void {
    // No need to implement anything here
  }

  writeValue(/*obj: any*/): void {
    // No need to implement anything here
  }

}
